if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      constructor() {
        super();
        this.url = this.dataset.url;
        this.intent = this.dataset.intent || 'related';
        this.limit = parseInt(this.dataset.limit, 10) || 4;
        this.currentSeed = null;
        this.onAddClick = this.onAddClick.bind(this);
        this.addEventListener('click', this.onAddClick);
      }

      get inDrawer() {
        return Boolean(this.closest('cart-drawer'));
      }

      connectedCallback() {
        this.loadRecommendations();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event && event.source === 'cart-recommendations') return;
            this.loadRecommendations();
          });
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      async loadRecommendations() {
        if (!this.url) return;

        try {
          const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
          const items = cart.items || [];

          if (items.length === 0) {
            this.reset();
            return;
          }

          const cartProductIds = items.map((item) => item.product_id);
          const seed = items[0].product_id;

          // Re-filter existing markup without a network call when the seed is unchanged.
          if (seed === this.currentSeed && this.dataset.loaded === 'true') {
            this.filterCards(cartProductIds);
            return;
          }

          this.currentSeed = seed;

          const requestUrl = `${this.url}?section_id=cart-recommendations&intent=${encodeURIComponent(
            this.intent
          )}&product_id=${seed}&limit=${this.limit}`;

          const text = await fetch(requestUrl).then((response) => response.text());
          const inner = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.cart-recommendations__inner');

          if (!inner) {
            this.reset();
            return;
          }

          this.innerHTML = inner.outerHTML;
          this.dataset.loaded = 'true';
          this.filterCards(cartProductIds);
        } catch (error) {
          console.error('cart-recommendations:', error);
          this.reset();
        }
      }

      filterCards(cartProductIds) {
        const ids = cartProductIds.map(String);
        let visible = 0;

        this.querySelectorAll('[data-product-id]').forEach((item) => {
          if (ids.includes(item.dataset.productId)) {
            item.remove();
          } else {
            visible += 1;
          }
        });

        this.hidden = visible === 0;
      }

      reset() {
        this.innerHTML = '';
        this.dataset.loaded = 'false';
        this.currentSeed = null;
        this.hidden = true;
      }

      onAddClick(event) {
        const button = event.target.closest('.cart-rec-card__add[data-variant-id]');
        if (!button) return;

        event.preventDefault();
        this.addToCart(button);
      }

      getSectionsToRender() {
        if (this.inDrawer) {
          return [
            { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
            { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
          ];
        }

        const sections = [];
        const mainItems = document.getElementById('main-cart-items');
        const mainFooter = document.getElementById('main-cart-footer');

        if (mainItems) {
          sections.push({ id: 'main-cart-items', section: mainItems.dataset.id, selector: '.js-contents' });
        }
        if (mainFooter) {
          sections.push({ id: 'main-cart-footer', section: mainFooter.dataset.id, selector: '.js-contents' });
        }
        sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });

        return sections;
      }

      async addToCart(button) {
        const variantId = button.dataset.variantId;
        const spinner = button.querySelector('.loading__spinner');

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');

        const sections = this.getSectionsToRender();

        try {
          const response = await fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: sections.map((section) => section.section),
              sections_url: window.location.pathname,
            }),
          }).then((res) => res.json());

          if (response.status) {
            console.error('cart-recommendations: add failed', response.description || response.message);
            return;
          }

          this.renderCart(response, sections);

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-recommendations',
              productVariantId: variantId,
              cartData: response,
            });
          }

          // Drawer is fully re-rendered (and re-initialises a fresh element);
          // only the page-embedded element needs a manual refresh.
          if (!this.inDrawer) this.loadRecommendations();
        } catch (error) {
          console.error('cart-recommendations:', error);
        } finally {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      renderCart(response, sections) {
        if (!response.sections) return;

        if (this.inDrawer) {
          const drawer = this.closest('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents(response);
          }
          return;
        }

        sections.forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container || !response.sections[section.section]) return;

          const target = container.querySelector(section.selector) || container;
          const source = new DOMParser()
            .parseFromString(response.sections[section.section], 'text/html')
            .querySelector(section.selector);

          if (target && source) target.innerHTML = source.innerHTML;
        });
      }
    }
  );
}
